import { Helmet } from "react-helmet-async";
import { categories } from "../data/mediaData";
import { useLightbox } from "../context/LightboxContext";
import BackToTop from "../components/BackToTop";
import BackButton from "../components/BackButton";
import { useState } from "react";

export default function Favorites() {
    const showLightbox = useLightbox();
    const [activeCategory, setActiveCategory] = useState("all");
    const [selectedTags, setSelectedTags] = useState([]);
    const [copiedTitle, setCopiedTitle] = useState(null);

    const handleCopy = (title, e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(title).then(() => {
            setCopiedTitle(title);
            setTimeout(() => {
                setCopiedTitle(null);
            }, 2000);
        }).catch(err => {
            console.error("Failed to copy title: ", err);
        });
    };

    // Get all unique tags from currently active category/categories
    const relevantCategories = activeCategory === "all" ? categories : categories.filter(cat => cat.id === activeCategory);
    const allAvailableTags = Array.from(new Set(
        relevantCategories.flatMap(cat => cat.items.flatMap(item => item.tags || []))
    )).sort();

    const toggleTag = (tag) => {
        setSelectedTags(prev =>
            prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
        );
    };

    const filteredCategories = relevantCategories.map(category => ({
        ...category,
        items: category.items.filter(item => {
            if (selectedTags.length === 0) return true;
            return selectedTags.every(tag => (item.tags || []).includes(tag));
        })
    })).filter(cat => cat.items.length > 0);

    return (
        <div id="favorites-page">
            <Helmet>
                <title>My Ultimate Cinematic Vault | Charlie GP.</title>
            </Helmet>

            <section className="page-head">
                <BackButton />
                <h1 className="page-title" style={{ marginTop: 0 }}>Vault<span className="dot">.</span></h1>
                <p className="page-sub">A curated collection of my all-time favorite TV series, movies, and cinematic masterpieces.</p>
            </section>

            <div className="category-filters reveal delay-1">
                <button
                    className={`filter-btn ${activeCategory === "all" ? "active" : ""}`}
                    onClick={() => {
                        setActiveCategory("all");
                        setSelectedTags([]);
                    }}
                >
                    ALL
                </button>
                {categories.map(cat => (
                    <button
                        key={cat.id}
                        className={`filter-btn ${activeCategory === cat.id ? "active" : ""}`}
                        onClick={() => {
                            setActiveCategory(cat.id);
                            setSelectedTags([]);
                        }}
                    >
                        {cat.title.toUpperCase()}
                    </button>
                ))}
            </div>

            {allAvailableTags.length > 0 && (
                <div className="tag-filters reveal delay-2">
                    <div className="tag-group">
                        {allAvailableTags.map(tag => (
                            <button
                                key={tag}
                                className={`tag-pill ${selectedTags.includes(tag) ? "active" : ""}`}
                                onClick={() => toggleTag(tag)}
                            >
                                #{tag.toUpperCase()}
                            </button>
                        ))}
                        {selectedTags.length > 0 && (
                            <button
                                className="clear-tags-icon"
                                onClick={() => setSelectedTags([])}
                                title="Clear All Filters"
                                aria-label="Clear All Filters"
                            >
                                [CLEAR FILTER]
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className="vault-container">
                {filteredCategories.map((category, catIndex) => (
                    <div key={category.id} className={`vault-section reveal delay-${catIndex + 3}`}>
                        <h2 className="vault-category-title">{category.title}</h2>
                        <div className="vault-grid">
                            {category.items.sort((a, b) => b.year - a.year).map((item, index) => (
                                <div
                                    key={index}
                                    className={`vault-card-wrapper ${item.tags?.includes('film-noir') ? 'is-film-noir' : ''}`}
                                    onClick={(e) => {
                                        if (item.image && !e.target.classList.contains('item-tag') && !e.target.closest('.vault-copy-btn')) {
                                            e.preventDefault();
                                            showLightbox(item.image, item.title);
                                        }
                                    }}
                                    role="button"
                                    tabIndex="0"
                                    aria-label={`View ${item.title} poster`}
                                >
                                    <div className="vault-card">
                                        <button
                                            className={`vault-copy-btn ${copiedTitle === item.title ? 'copied' : ''}`}
                                            onClick={(e) => handleCopy(item.title, e)}
                                            title="Copy Title to Clipboard"
                                            aria-label={`Copy ${item.title} to clipboard`}
                                        >
                                            <i className={`bi ${copiedTitle === item.title ? 'bi-clipboard-check' : 'bi-clipboard'}`}></i>
                                        </button>
                                        <div className="vault-card-info">
                                            <div className="vault-card-top">
                                                <div className="vault-meta-left">
                                                    <span className="vault-year">{item.year}</span>
                                                    {item.rating && <span className="vault-rating-badge">{item.rating}</span>}
                                                </div>
                                                <span className="vault-rating">IMDB {item.imdb}</span>
                                            </div>
                                            <h3 className="vault-item-title">{item.title}</h3>
                                            <div className="item-tags">
                                                {(item.tags || []).map(tag => (
                                                    <span
                                                        key={tag}
                                                        className={`item-tag ${selectedTags.includes(tag) ? "active" : ""}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleTag(tag);
                                                        }}
                                                    >
                                                        #{tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        {item.image && (
                                            <div className="vault-poster-container">
                                                <img
                                                    src={item.image}
                                                    alt={item.title}
                                                    className="vault-poster-image"
                                                    loading="lazy"
                                                    onError={(e) => {
                                                        const container = e.target.closest('.vault-poster-container');
                                                        if (container) container.style.display = 'none';
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <div className="vault-card-overlay"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
                {filteredCategories.length === 0 && (
                    <div className="no-results reveal">
                        <p>No titles match all your selected filters.</p>
                        <button className="read-more-link" onClick={() => setSelectedTags([])}>Reset Filters</button>
                    </div>
                )}
            </div>

            <BackToTop />
        </div>
    );
}
